import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Calculator, TrendingUp, Sparkles, X, Building2, Send, Users, ShieldCheck, BarChart3 } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "29",
    period: "/ mese",
    badge: "Per uffici fino a 50",
    description: "Pensato per uffici piccoli che vogliono partire bene da subito.",
    cta: "Inizia gratis 30 giorni",
    highlight: false,
    features: [
      "Fino a 50 utenti",
      "1 bot Telegram dedicato",
      "Prenotazioni illimitate",
      "Punti di ritiro multipli",
      "Notifiche cutoff automatiche",
      "Supporto via email",
    ],
    notIncluded: ["Report mensile", "Onboarding assistito"],
  },
  {
    id: "pro",
    name: "Pro",
    price: "79",
    period: "/ mese",
    badge: "Più scelto",
    description: "Per uffici strutturati che vogliono il pieno controllo e i report.",
    cta: "Attiva il piano Pro",
    highlight: true,
    features: [
      "Utenti illimitati",
      "Bot Telegram personalizzato",
      "Menu ricorrenti settimanali",
      "Report mensili (PDF + CSV)",
      "Riepilogo posate per preparatore",
      "Onboarding admin assistito",
      "Supporto prioritario",
      "SLA risposta 24h",
    ],
    notIncluded: [],
  },
  {
    id: "custom",
    name: "Enterprise",
    price: "Su misura",
    period: "",
    badge: "Enti pubblici · Ordini · Forze armate",
    description: "Per organizzazioni grandi o con vincoli specifici (CONSIP, MePA, formazione interna).",
    cta: "Richiedi preventivo",
    highlight: false,
    features: [
      "Tutto del piano Pro",
      "Multi-sede coordinata",
      "Audit log esteso",
      "Formazione preparatori on-site",
      "Contratto su carta intestata",
      "SLA personalizzato",
      "Account manager dedicato",
      "Fatturazione PA / split payment",
    ],
    notIncluded: [],
  },
];

export default function Pricing() {
  // Calcolatore
  const [headcount, setHeadcount] = useState(30);
  const adoption = 0.6;
  const dailySaving = 5; // € per pasto risparmiati vs bar
  const workingDays = 22;
  const monthlyUsers = Math.round(headcount * adoption);
  const monthlySaving = monthlyUsers * dailySaving * workingDays;
  const annualSaving = monthlySaving * 12;
  const recommendedPlan = headcount <= 50 ? PLANS[0] : headcount <= 150 ? PLANS[1] : PLANS[2];
  const planCostAnnual =
    recommendedPlan.id === "custom" ? 0 : Number(recommendedPlan.price) * 12;
  const netSaving = annualSaving - planCostAnnual;

  return (
    <div className="min-h-screen bg-background bg-grain">
      <PublicHeader ctaTo="/contatti" ctaLabel="Richiedi attivazione" />

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-20 animate-fade-in">
        {/* Hero */}
        <section className="text-center mb-16 sm:mb-20" data-testid="pricing-hero">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-5">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Prezzi trasparenti
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl mx-auto mb-5">
            Costa meno di un caffè a settimana,
            <br />
            <span className="text-accent">vale molto di più.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Schiscetta si paga da sola. Un solo collega che smette di andare al bar copre il costo
            di tutto l'ufficio per un mese.
          </p>
        </section>

        {/* Calcolatore */}
        <section className="bg-primary text-white rounded-2xl p-7 sm:p-10 mb-16 sm:mb-20 relative overflow-hidden" data-testid="pricing-calculator">
          <div className="absolute -right-12 -top-12 w-64 h-64 bg-white/5 rounded-full" />
          <div className="absolute -left-16 -bottom-16 w-72 h-72 bg-white/5 rounded-full" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold mb-3 opacity-80">
                <Calculator className="w-4 h-4" /> Calcolatore di risparmio
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight mb-3">
                Quanto risparmierà il tuo ufficio?
              </h2>
              <p className="text-white/85 leading-relaxed mb-6 max-w-md">
                Trascina lo slider e scopri quanto risparmieranno i tuoi colleghi in un anno,
                rispetto al bar o alle mense esterne.
              </p>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span>Dipendenti in ufficio</span>
                    <span className="font-display text-2xl font-semibold tabular-nums">{headcount}</span>
                  </div>
                  <input
                    type="range" min="5" max="500" step="5"
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value))}
                    data-testid="pricing-headcount-slider"
                    className="w-full accent-white"
                  />
                  <div className="flex justify-between text-[10px] uppercase tracking-widest mt-1 opacity-60">
                    <span>5</span><span>500+</span>
                  </div>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">
                  Assumiamo che il 60% dei dipendenti usi il servizio (utenti reali in altri uffici),
                  con un risparmio medio di {dailySaving} € a pasto rispetto al bar o ai posti convenzionati,
                  su {workingDays} giorni lavorativi al mese.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="pricing-calculator-results">
              <ResultCard label="Pasti al mese" value={monthlyUsers * workingDays} suffix="" />
              <ResultCard label="Risparmio mensile" value={monthlySaving} suffix=" €" highlight />
              <ResultCard label="Risparmio annuo" value={annualSaving} suffix=" €" highlight />
              <ResultCard label="Piano consigliato" value={recommendedPlan.name} suffix="" custom />
              <div className="col-span-2 bg-white/10 border border-white/15 rounded-lg p-4 sm:p-5 mt-2">
                <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Net saving (anno)</div>
                <div className="font-display text-3xl sm:text-4xl font-semibold tabular-nums" data-testid="pricing-net-saving">
                  {recommendedPlan.id === "custom" ? "Su preventivo" : `${netSaving.toLocaleString("it-IT")} €`}
                </div>
                <div className="text-xs opacity-70 mt-1">
                  Risparmio totale meno costo della piattaforma (12 mesi)
                </div>
              </div>
              <Link
                to={`/contatti?plan=${recommendedPlan.id}&headcount=${headcount}`}
                data-testid="pricing-cta-calculator"
                className="col-span-2 mt-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-primary font-medium rounded-md hover:bg-white/90 active:scale-[0.98] transition-all"
              >
                Richiedi attivazione · {recommendedPlan.name} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="mb-16 sm:mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
              Tre piani. Zero sorprese.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Prezzo unico per ufficio (non per utente). Nessuna commissione sulle prenotazioni.
              30 giorni gratis su Starter e Pro, senza carta di credito.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 lg:gap-6" data-testid="pricing-plans">
            {PLANS.map((p) => (
              <PlanCard key={p.id} plan={p} headcount={headcount} />
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            Prezzi IVA esclusa. Fatturazione mensile o annuale (10% di sconto annuale).
          </p>
        </section>

        {/* Case study / Numeri reali */}
        <section className="bg-card border border-border rounded-2xl p-7 sm:p-10 mb-16 sm:mb-20" data-testid="pricing-case-study">
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-3">
                Caso d'uso
              </div>
              <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight mb-4">
                Quando l'ufficio diventa il pranzo che vorresti.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                Schiscetta è nata in un ufficio pubblico italiano dove tre colleghi cucinavano già
                per amici e parenti durante la pausa. Il problema non era il cibo: era
                <strong className="text-foreground"> contare le porzioni, raccogliere i soldi e ricordarsi chi voleva cosa</strong>.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                In due settimane dal lancio: zero foglietti, zero WhatsApp, ordini gestiti in
                10 secondi dal cellulare, e il preparatore che arriva sapendo già quante porzioni e
                quanti tovaglioli mettere in borsa.
              </p>
            </div>
            <div className="lg:col-span-3 grid sm:grid-cols-3 gap-3 sm:gap-4">
              <BigStat value="~5 €" label="Risparmio medio per pasto" />
              <BigStat value="< 10 sec" label="Tempo per prenotare dal bot" />
              <BigStat value="0" label="Foglietti, mail di gruppo o WhatsApp" />
              <BigStat value="60%" label="Adesione media in ufficio" />
              <BigStat value="22 gg" label="Giorni lavorativi attivi/mese" />
              <BigStat value="100%" label="Soddisfazione preparatori (n. piccolo)" />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-semibold tracking-tight mb-6 text-center">
            Domande frequenti
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            <Faq q="Chi paga: l'ufficio o i singoli colleghi?">
              L'abbonamento Schiscetta lo paga <strong>una sola entità</strong> per ufficio
              (responsabile, associazione interna, fondo cassa). I singoli colleghi <strong>non pagano nulla</strong> alla piattaforma — pagano solo i pasti direttamente a chi cucina, come han sempre fatto.
            </Faq>
            <Faq q="Come avviene l'attivazione di un nuovo ufficio?">
              Compili il form sulla pagina <Link to="/contatti" className="text-primary hover:underline">Contatti</Link>.
              Ti rispondiamo entro 24 ore lavorative, configuriamo il bot Telegram dedicato della tua sede e mandiamo le credenziali al responsabile via email. Da quel momento la tua sede è isolata, sicura, indipendente.
            </Faq>
            <Faq q="Posso provare prima di pagare?">
              Sì. <strong>30 giorni gratis</strong> sui piani Starter e Pro, senza carta di credito richiesta. Al termine del periodo decidi se attivare l'abbonamento o disattivare la sede senza costi.
            </Faq>
            <Faq q="Cosa succede se cancello l'abbonamento?">
              I dati restano accessibili in sola lettura per 30 giorni, poi vengono cancellati. Puoi esportare ordini e utenti in CSV in qualsiasi momento.
            </Faq>
            <Faq q="Lavoriamo in un ente pubblico: si può fatturare via MePA / CONSIP?">
              Sì, sul piano Enterprise. Emettiamo fattura elettronica con split payment e siamo
              compatibili con gli acquisti su MePA. Scrivici da <Link to="/contatti" className="text-primary hover:underline">Contatti</Link> selezionando "Enterprise".
            </Faq>
            <Faq q="Quanti bot Telegram servono?">
              <strong>Uno per ufficio.</strong> Lo creiamo noi via BotFather durante l'onboarding,
              oppure puoi configurarlo tu dall'area Admin. Il bot ha utenti, comandi e statistiche
              completamente separati dagli altri uffici.
            </Faq>
            <Faq q="I dati dei dipendenti come vengono trattati?">
              Memorizziamo solo nome, email aziendale, ruolo e prenotazioni effettuate. Niente terze parti, niente analytics esterni, niente pubblicità.
              Server in UE, conforme GDPR. Ogni ufficio ha i propri dati strettamente isolati.
            </Faq>
          </div>
        </section>

        {/* CTA finale */}
        <section className="text-center py-10 sm:py-16 bg-primary/5 border border-primary/15 rounded-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
            Pronto a far pranzare meglio i tuoi colleghi?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            30 giorni gratis. Onboarding del bot in 10 minuti. Nessun impegno, nessuna carta.
          </p>
          <Link
            to="/contatti?plan=pro"
            data-testid="pricing-cta-bottom"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all"
          >
            Inizia la prova gratuita <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="text-xs text-muted-foreground mt-4">
            Hai già un account? <Link to="/login" className="text-primary hover:underline">Accedi</Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center text-xs text-muted-foreground">
          Schiscetta · piattaforma interna per uffici · 2026 · IVA inclusa fatturazione su richiesta
        </div>
      </footer>
    </div>
  );
}

function ResultCard({ label, value, suffix = "", highlight, custom }) {
  return (
    <div className={`rounded-lg p-4 ${highlight ? "bg-white text-primary" : "bg-white/10 border border-white/15"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${highlight ? "opacity-70" : "opacity-70"}`}>
        {label}
      </div>
      <div className={`font-display font-semibold tabular-nums ${custom ? "text-2xl" : "text-2xl"}`}>
        {typeof value === "number" ? value.toLocaleString("it-IT") : value}
        {suffix}
      </div>
    </div>
  );
}

function PlanCard({ plan, headcount }) {
  const highlightTone = plan.highlight ? "border-primary shadow-lg scale-[1.02]" : "border-border";
  return (
    <article
      data-testid={`pricing-plan-${plan.id}`}
      className={`bg-card border-2 rounded-xl p-6 flex flex-col relative ${highlightTone}`}
    >
      {plan.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] uppercase tracking-widest font-semibold bg-primary text-white rounded-full">
          {plan.badge}
        </span>
      )}
      {!plan.highlight && (
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
          {plan.badge}
        </span>
      )}
      <h3 className="font-display text-2xl font-semibold tracking-tight mb-1.5">{plan.name}</h3>
      <p className="text-sm text-muted-foreground mb-5 min-h-[40px]">{plan.description}</p>
      <div className="mb-5">
        {plan.price === "Su misura" ? (
          <div className="font-display text-3xl font-semibold tracking-tight">Su misura</div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="font-display text-4xl font-semibold tabular-nums">€{plan.price}</span>
            <span className="text-muted-foreground text-sm">{plan.period}</span>
          </div>
        )}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
        {plan.notIncluded.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground/60">
            <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="line-through">{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/contatti?plan=${plan.id}&headcount=${headcount}`}
        data-testid={`pricing-cta-${plan.id}`}
        className={`block text-center py-2.5 rounded-md font-medium text-sm transition-all ${
          plan.highlight
            ? "bg-primary text-white hover:bg-[#436343]"
            : "bg-white border border-border hover:border-primary/40 text-foreground"
        }`}
      >
        {plan.cta}
      </Link>
    </article>
  );
}

function BigStat({ value, label }) {
  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 leading-snug">{label}</div>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="bg-card border border-border rounded-lg group" data-testid="pricing-faq">
      <summary className="cursor-pointer p-4 sm:p-5 font-medium flex items-center justify-between gap-3 list-none">
        <span>{q}</span>
        <span className="text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-4 sm:px-5 pb-5 text-sm text-muted-foreground leading-relaxed -mt-1">
        {children}
      </div>
    </details>
  );
}
